import React, { useState, useEffect, useCallback } from 'react';
import { Bell, User, Settings, Moon, Sun, LogOut, Check, CheckCheck, Send, ChevronRight, Clock, AlertTriangle, RefreshCw } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// API client
const api = {
  token: null,
  setToken(token) { this.token = token; },
  async fetch(endpoint, options = {}) {
    const res = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(this.token && { Authorization: `Bearer ${this.token}` }),
        ...options.headers,
      },
    });
    if (!res.ok) throw new Error(`API Error: ${res.status}`);
    return res.json();
  },
  getTeam(practiceId) { return this.fetch(`/api/watch/team?practice_id=${practiceId}`); },
  login(userId) { return this.fetch('/api/watch/login', { method: 'POST', body: JSON.stringify({ user_id: userId }) }); },
  getAlerts() { return this.fetch('/api/watch/alerts'); },
  markRead(alertId) { return this.fetch(`/api/alerts/${alertId}/read`, { method: 'POST' }); },
  advanceAlert(alertId) { return this.fetch(`/api/watch/advance-alert/${alertId}`, { method: 'POST' }); },
  quickResponse(alertId, responseType, minutes = null) {
    return this.fetch('/api/watch/quick-response', {
      method: 'POST',
      body: JSON.stringify({ alert_id: alertId, response_type: responseType, minutes })
    });
  },
};

// Utility for timer display
const formatTimer = (startTime) => {
  if (!startTime) return '00:00';
  const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
  const mins = Math.floor(elapsed / 60);
  const secs = elapsed % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
};

// Color based on elapsed time
const getTimerColor = (startTime, warning = 300, critical = 420) => {
  if (!startTime) return 'text-green-500';
  const elapsed = Math.floor((Date.now() - new Date(startTime).getTime()) / 1000);
  if (elapsed >= critical) return 'text-red-600 animate-pulse';
  if (elapsed >= warning) return 'text-red-500';
  if (elapsed >= warning * 0.6) return 'text-amber-500';
  return 'text-green-500';
};

// Login Screen Component
function LoginScreen({ onLogin, isDarkMode, practiceId = null }) {
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputPracticeId, setInputPracticeId] = useState('');
  const [showPracticeInput, setShowPracticeInput] = useState(!practiceId);

  const fetchTeam = useCallback(async (pId) => {
    if (!pId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getTeam(pId);
      // Add practice_id to each team member for login
      const teamWithPractice = (data.team || []).map(m => ({
        ...m,
        practice_id: data.practice_id || pId
      }));
      setTeamMembers(teamWithPractice);
      setShowPracticeInput(false);
    } catch (err) {
      setError('Failed to load team. Check practice ID.');
      setTeamMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (practiceId) fetchTeam(practiceId);
    else setLoading(false);
  }, [practiceId, fetchTeam]);

  const handleLogin = async (user) => {
    try {
      setLoading(true);
      const result = await api.login(user.id);
      const token = result.access_token || result.token;
      if (token) {
        api.setToken(token);
        localStorage.setItem('mobileAppToken', token);
        // Merge practice_id from the list with the returned user data
        const userData = { ...user, ...result.user };
        localStorage.setItem('mobileAppUser', JSON.stringify(userData));
        localStorage.setItem('mobileAppPracticeId', userData.practice_id || user.practice_id);
        onLogin(userData, token);
      } else {
        setError('Login failed: No token received');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const bg = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textPrimary = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const textSecondary = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const cardBg = isDarkMode ? 'bg-slate-800' : 'bg-white';

  if (showPracticeInput) {
    return (
      <div className={`min-h-screen ${bg} p-4 flex flex-col items-center justify-center`}>
        <div className="w-full max-w-sm">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-cyan-600 rounded-2xl mx-auto mb-4 flex items-center justify-center">
              <Bell className="w-8 h-8 text-white" />
            </div>
            <h1 className={`text-2xl font-bold ${textPrimary}`}>DentalPulse Mobile</h1>
            <p className={textSecondary}>Enter your practice ID to continue</p>
          </div>
          <div className={`${cardBg} rounded-xl p-4 shadow-lg`}>
            <input
              type="text"
              value={inputPracticeId}
              onChange={(e) => setInputPracticeId(e.target.value)}
              placeholder="Practice ID"
              className={`w-full p-3 rounded-lg border ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'} mb-4`}
            />
            <button
              onClick={() => fetchTeam(inputPracticeId)}
              disabled={!inputPracticeId}
              className="w-full py-3 bg-cyan-600 text-white rounded-lg font-medium disabled:opacity-50"
            >
              Continue
            </button>
            {error && <p className="text-red-500 text-sm mt-2 text-center">{error}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${bg} p-4`}>
      <div className="text-center mb-6">
        <h1 className={`text-xl font-bold ${textPrimary}`}>Select User</h1>
        <p className={textSecondary}>Tap your name to login</p>
      </div>
      
      {loading ? (
        <div className="flex justify-center py-8">
          <RefreshCw className={`w-8 h-8 animate-spin ${textSecondary}`} />
        </div>
      ) : error ? (
        <div className={`${cardBg} rounded-xl p-4 text-center`}>
          <p className="text-red-500">{error}</p>
          <button onClick={() => setShowPracticeInput(true)} className="text-cyan-500 mt-2">
            Change Practice
          </button>
        </div>
      ) : (
        <div className="space-y-2">
          {teamMembers.map((member) => (
            <button
              key={member.id}
              onClick={() => handleLogin(member)}
              className={`w-full ${cardBg} rounded-xl p-4 flex items-center gap-3 shadow-sm active:scale-98 transition-transform`}
            >
              <div className="w-10 h-10 bg-cyan-100 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-cyan-600" />
              </div>
              <div className="flex-1 text-left">
                <p className={`font-medium ${textPrimary}`}>{member.name || `${member.first_name} ${member.last_name}`}</p>
                <p className={`text-sm ${textSecondary} capitalize`}>{member.role}</p>
              </div>
              <ChevronRight className={`w-5 h-5 ${textSecondary}`} />
            </button>
          ))}
          <button 
            onClick={() => setShowPracticeInput(true)} 
            className={`w-full text-center py-3 ${textSecondary} text-sm`}
          >
            Change Practice
          </button>
        </div>
      )}
    </div>
  );
}

// Alert Card Component
function AlertCard({ alert, onMarkRead, onAdvance, onQuickResponse, isDarkMode }) {
  const [showQuickResponse, setShowQuickResponse] = useState(false);
  const [timer, setTimer] = useState(formatTimer(alert.created_at));
  
  const cardBg = isDarkMode ? 'bg-slate-800' : 'bg-white';
  const textPrimary = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const textSecondary = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  useEffect(() => {
    const interval = setInterval(() => {
      setTimer(formatTimer(alert.created_at));
    }, 1000);
    return () => clearInterval(interval);
  }, [alert.created_at]);

  const timerColor = getTimerColor(alert.created_at);
  
  const alertTypeLabels = {
    patient_arrived: 'Patient Arrived',
    seated_in_room: 'Patient Seated',
    dr_needed: 'Dr. Needed',
    help_needed: 'Help Needed',
    custom_button: 'Custom Alert',
  };

  const alertTypeColors = {
    patient_arrived: 'bg-sky-500',
    seated_in_room: 'bg-emerald-500',
    dr_needed: 'bg-amber-500',
    help_needed: 'bg-violet-500',
    custom_button: 'bg-indigo-500',
  };

  // What happens when you advance each alert type
  const advanceLabels = {
    patient_arrived: 'Seat Patient',
    seated_in_room: 'Check Out',
    dr_needed: "I'm Here",
    help_needed: 'Handled',
  };

  const quickResponses = [
    { type: 'on_my_way', label: 'On my way' },
    { type: 'be_there_in', label: '1 min', minutes: 1 },
    { type: 'be_there_in', label: '2 min', minutes: 2 },
    { type: 'be_there_in', label: '5 min', minutes: 5 },
  ];

  return (
    <div className={`${cardBg} rounded-xl shadow-sm overflow-hidden ${alert.is_for_me ? 'ring-2 ring-emerald-500' : ''}`}>
      {/* Alert Header */}
      <div className={`${alertTypeColors[alert.alert_type] || 'bg-gray-500'} px-4 py-2 flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className="text-white font-medium text-sm">
            {alertTypeLabels[alert.alert_type] || alert.alert_type}
          </span>
          {alert.is_for_me && (
            <span className="text-xs bg-white/20 text-white px-2 py-0.5 rounded-full">
              For You
            </span>
          )}
        </div>
        <span className={`font-mono font-bold text-white`}>{timer}</span>
      </div>

      {/* Alert Body */}
      <div className="p-4">
        <h3 className={`font-semibold ${textPrimary}`}>{alert.operatory_name || `Op ${alert.operatory_id}`}</h3>
        {alert.metadata?.doctor_name && (
          <p className={textSecondary}>Dr. {alert.metadata.doctor_name}</p>
        )}
        {alert.metadata?.message && (
          <p className={`${textSecondary} text-sm mt-1`}>{alert.metadata.message}</p>
        )}

        {/* Read Receipts */}
        {alert.read_receipts?.length > 0 && (
          <div className={`mt-2 flex items-center gap-1 ${textSecondary} text-xs`}>
            <CheckCheck className="w-3 h-3 text-cyan-500" />
            <span>Read by {alert.read_receipts.map(r => r.user_name).join(', ')}</span>
          </div>
        )}

        {/* Quick Responses Received */}
        {alert.quick_responses?.length > 0 && (
          <div className={`mt-2 space-y-1`}>
            {alert.quick_responses.map((qr, i) => (
              <div key={i} className={`text-xs ${textSecondary} flex items-center gap-1`}>
                <Send className="w-3 h-3" />
                <span>{qr.user_name}: {qr.message || qr.response_type}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className={`px-4 pb-4 flex gap-2`}>
        <button
          onClick={() => onMarkRead(alert.id)}
          className={`flex-1 py-2 ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} rounded-lg text-sm font-medium flex items-center justify-center gap-1 ${textPrimary}`}
          data-testid={`alert-${alert.id}-read`}
        >
          <Check className="w-4 h-4" /> Read
        </button>
        {advanceLabels[alert.alert_type] && (
          <button
            onClick={() => onAdvance(alert.id)}
            className="flex-1 py-2 bg-emerald-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1"
            data-testid={`alert-${alert.id}-advance`}
          >
            <ChevronRight className="w-4 h-4" /> {advanceLabels[alert.alert_type]}
          </button>
        )}
        <button
          onClick={() => setShowQuickResponse(!showQuickResponse)}
          className="flex-1 py-2 bg-cyan-500 text-white rounded-lg text-sm font-medium flex items-center justify-center gap-1"
          data-testid={`alert-${alert.id}-reply`}
        >
          <Send className="w-4 h-4" /> Reply
        </button>
      </div>

      {/* Quick Response Options */}
      {showQuickResponse && (
        <div className={`px-4 pb-4 border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}>
          <p className={`text-xs ${textSecondary} py-2`}>Quick responses:</p>
          <div className="flex flex-wrap gap-2">
            {quickResponses.map((qr, i) => (
              <button
                key={i}
                onClick={() => {
                  onQuickResponse(alert.id, qr.type, qr.minutes);
                  setShowQuickResponse(false);
                }}
                className={`px-3 py-1.5 rounded-full text-sm ${isDarkMode ? 'bg-slate-700' : 'bg-slate-100'} ${textPrimary}`}
              >
                {qr.label}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// Alerts Screen Component
function AlertsScreen({ user, isDarkMode }) {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const bg = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textPrimary = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const textSecondary = isDarkMode ? 'text-slate-400' : 'text-slate-500';

  const fetchAlerts = useCallback(async () => {
    try {
      const data = await api.getAlerts();
      setAlerts(data.alerts || []);
      setError(null);
    } catch (err) {
      setError('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 5000);
    return () => clearInterval(interval);
  }, [fetchAlerts]);

  const handleMarkRead = async (alertId) => {
    try {
      await api.markRead(alertId);
      fetchAlerts();
    } catch (err) {
      console.error('Failed to mark as read:', err);
    }
  };

  const handleAdvance = async (alertId) => {
    try {
      await api.advanceAlert(alertId);
      fetchAlerts();
    } catch (err) {
      console.error('Failed to advance alert:', err);
    }
  };

  const handleQuickResponse = async (alertId, type, minutes) => {
    try {
      await api.quickResponse(alertId, type, minutes);
      fetchAlerts();
    } catch (err) {
      console.error('Failed to send quick response:', err);
    }
  };

  return (
    <div className={`min-h-screen ${bg} pb-20`}>
      <div className="p-4">
        <div className="flex items-center justify-between mb-4">
          <h1 className={`text-xl font-bold ${textPrimary}`}>Active Alerts</h1>
          <button onClick={fetchAlerts} className={`p-2 ${textSecondary}`}>
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading && alerts.length === 0 ? (
          <div className="flex justify-center py-12">
            <RefreshCw className={`w-8 h-8 animate-spin ${textSecondary}`} />
          </div>
        ) : error ? (
          <div className={`${isDarkMode ? 'bg-slate-800' : 'bg-white'} rounded-xl p-6 text-center`}>
            <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-2" />
            <p className={textSecondary}>{error}</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className={`${isDarkMode ? 'bg-slate-800' : 'bg-white'} rounded-xl p-8 text-center`}>
            <Bell className={`w-12 h-12 ${textSecondary} mx-auto mb-2 opacity-50`} />
            <p className={textPrimary}>No active alerts</p>
            <p className={`text-sm ${textSecondary}`}>You're all caught up!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.map((alert) => (
              <AlertCard
                key={alert.id}
                alert={alert}
                onMarkRead={handleMarkRead}
                onAdvance={handleAdvance}
                onQuickResponse={handleQuickResponse}
                isDarkMode={isDarkMode}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Settings Screen Component
function SettingsScreen({ user, isDarkMode, onToggleDarkMode, onLogout }) {
  const bg = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const textPrimary = isDarkMode ? 'text-slate-100' : 'text-slate-900';
  const textSecondary = isDarkMode ? 'text-slate-400' : 'text-slate-500';
  const cardBg = isDarkMode ? 'bg-slate-800' : 'bg-white';

  return (
    <div className={`min-h-screen ${bg} p-4 pb-20`}>
      <h1 className={`text-xl font-bold ${textPrimary} mb-4`}>Settings</h1>

      {/* User Info */}
      <div className={`${cardBg} rounded-xl p-4 mb-4`}>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 bg-cyan-100 rounded-full flex items-center justify-center">
            <User className="w-6 h-6 text-cyan-600" />
          </div>
          <div>
            <p className={`font-semibold ${textPrimary}`}>{user?.name || `${user?.first_name || ''} ${user?.last_name || ''}`.trim() || 'User'}</p>
            <p className={`text-sm ${textSecondary} capitalize`}>{user?.role}</p>
          </div>
        </div>
      </div>

      {/* Settings List */}
      <div className={`${cardBg} rounded-xl overflow-hidden`}>
        {/* Dark Mode Toggle */}
        <button
          onClick={onToggleDarkMode}
          className={`w-full px-4 py-4 flex items-center justify-between border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-100'}`}
        >
          <div className="flex items-center gap-3">
            {isDarkMode ? <Moon className="w-5 h-5 text-cyan-500" /> : <Sun className="w-5 h-5 text-amber-500" />}
            <span className={textPrimary}>Dark Mode</span>
          </div>
          <div className={`w-12 h-7 rounded-full ${isDarkMode ? 'bg-cyan-500' : 'bg-slate-300'} relative transition-colors`}>
            <div className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-transform ${isDarkMode ? 'translate-x-5' : 'translate-x-0.5'}`} />
          </div>
        </button>

        {/* Logout */}
        <button
          onClick={onLogout}
          className="w-full px-4 py-4 flex items-center gap-3 text-red-500"
        >
          <LogOut className="w-5 h-5" />
          <span>Sign Out</span>
        </button>
      </div>

      {/* App Info */}
      <div className={`mt-6 text-center ${textSecondary} text-sm`}>
        <p>DentalPulse Mobile</p>
        <p>Version 1.0.0</p>
      </div>
    </div>
  );
}

// Main Mobile App Component
export default function MobileAppPage() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [activeTab, setActiveTab] = useState('alerts');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Load saved preferences
    const savedDarkMode = localStorage.getItem('mobileAppDarkMode');
    if (savedDarkMode) setIsDarkMode(JSON.parse(savedDarkMode));

    // Check for saved auth
    const savedToken = localStorage.getItem('mobileAppToken');
    const savedUser = localStorage.getItem('mobileAppUser');
    
    if (savedToken && savedUser) {
      api.setToken(savedToken);
      setUser(JSON.parse(savedUser));
      setIsLoggedIn(true);
    }
    setLoading(false);
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('mobileAppToken');
    localStorage.removeItem('mobileAppUser');
    api.setToken(null);
    setUser(null);
    setIsLoggedIn(false);
  };

  const handleToggleDarkMode = () => {
    const newMode = !isDarkMode;
    setIsDarkMode(newMode);
    localStorage.setItem('mobileAppDarkMode', JSON.stringify(newMode));
  };

  const bg = isDarkMode ? 'bg-slate-900' : 'bg-slate-50';
  const tabBg = isDarkMode ? 'bg-slate-800' : 'bg-white';
  const textSecondary = isDarkMode ? 'text-slate-500' : 'text-slate-400';

  if (loading) {
    return (
      <div className={`min-h-screen ${bg} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-16 h-16 bg-cyan-600 rounded-2xl mx-auto mb-4 flex items-center justify-center animate-pulse">
            <Bell className="w-8 h-8 text-white" />
          </div>
          <p className={textSecondary}>Loading...</p>
        </div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return (
      <LoginScreen
        onLogin={handleLogin}
        isDarkMode={isDarkMode}
        practiceId={localStorage.getItem('mobileAppPracticeId')}
      />
    );
  }

  return (
    <div className={`min-h-screen ${bg}`}>
      {/* Content */}
      {activeTab === 'user' && (
        <LoginScreen
          onLogin={handleLogin}
          isDarkMode={isDarkMode}
          practiceId={user?.practice_id}
        />
      )}
      {activeTab === 'alerts' && (
        <AlertsScreen user={user} isDarkMode={isDarkMode} />
      )}
      {activeTab === 'settings' && (
        <SettingsScreen
          user={user}
          isDarkMode={isDarkMode}
          onToggleDarkMode={handleToggleDarkMode}
          onLogout={handleLogout}
        />
      )}

      {/* Bottom Tab Bar */}
      <div className={`fixed bottom-0 left-0 right-0 ${tabBg} border-t ${isDarkMode ? 'border-slate-700' : 'border-slate-200'} px-4 py-2 pb-safe flex justify-around z-50`} style={{ paddingBottom: '20px' }}>
        <button
          data-testid="tab-user"
          onClick={() => setActiveTab('user')}
          className={`flex flex-col items-center py-1 px-4 ${activeTab === 'user' ? 'text-cyan-500' : textSecondary}`}
        >
          <User className="w-6 h-6" />
          <span className="text-xs mt-1">User</span>
        </button>
        <button
          data-testid="tab-alerts"
          onClick={() => setActiveTab('alerts')}
          className={`flex flex-col items-center py-1 px-4 ${activeTab === 'alerts' ? 'text-cyan-500' : textSecondary}`}
        >
          <Bell className="w-6 h-6" />
          <span className="text-xs mt-1">Alerts</span>
        </button>
        <button
          data-testid="tab-settings"
          onClick={() => setActiveTab('settings')}
          className={`flex flex-col items-center py-1 px-4 ${activeTab === 'settings' ? 'text-cyan-500' : textSecondary}`}
        >
          <Settings className="w-6 h-6" />
          <span className="text-xs mt-1">Settings</span>
        </button>
      </div>
    </div>
  );
}
